import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ScheduleUpdate, isJpHoliday, jstDate, stopNoteId, type Driver, type ScheduleItem, type Template, lockKeys } from '@torapon/shared';
import { api, HttpError } from '../lib/api';
import { useDirectory, useNames } from '../lib/directory';
import { putNote, readNote, rebindNotes, routineNoteId, scheduleNoteId, showPlacesToDriver } from '../lib/fs';
import { useMe } from '../lib/me';
import { useLive } from '../lib/realtime';
import { Dialog, useToast } from '../components/ui';
import { LockBanner, useEditLock, useLocks } from '../lib/locks';

const WEEK = ['日', '月', '火', '水', '木', '金', '土'];
const ymOf = (s: string) => s.slice(0, 7);
const shiftMonth = (ym: string, n: number) => { const [y, m] = ym.split('-').map(Number) as [number, number]; return new Date(Date.UTC(y, m - 1 + n, 1)).toISOString().slice(0, 7); };

/**
 * 月間スケジュール（仕様書「月間スケジュール」）：1 ヶ月のカレンダーに定期便の予定を時刻順に、担当の色で出す。
 * 日の予定を押すと、その日だけの変更（休み・担当・時刻）と今すぐ送るができる。担当未定は赤
 */
export function SchedulePage() {
  const today = jstDate(new Date().toISOString());
  const [sp, setSp] = useSearchParams();
  const month = sp.get('month') ?? ymOf(today);
  const names = useNames();
  const [items, setItems] = useState<ScheduleItem[] | null>(null);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [open, setOpen] = useState<ScheduleItem | null>(null);
  const [err, setErr] = useState('');
  // 月を変えた直後に前の月の結果が遅れて届いても、いま見ている月の結果だけを出す
  const seq = useRef(0);
  const load = () => {
    const n = ++seq.current;
    return api.get<{ schedules: ScheduleItem[] }>(`/v1/office/schedules?month=${month}`)
      .then((r) => { if (n === seq.current) { setItems(r.schedules); setErr(''); } })
      .catch((e) => { if (n === seq.current) setErr(e instanceof HttpError ? e.message : '読み込めませんでした'); });
  };
  useEffect(() => { setItems(null); void load(); }, [month]);
  useEffect(() => { api.get<{ drivers: Driver[] }>('/v1/office/drivers').then((r) => setDrivers(r.drivers)).catch(() => {}); }, []);
  useLive(['deliveries'], load);
  const color = useMemo(() => new Map(drivers.map((d) => [d.id, d.color])), [drivers]);
  // ほかの人が編集中のドライバー・日には、編集者の名前を出す
  const locks = useLocks();
  const lockedBy = (driverId: string | null, d: string) => (driverId ? locks.find((l) => l.key === lockKeys.driverDay(driverId, d))?.by : undefined);
  // 月曜はじまりではなく、日曜はじまり（カレンダーの見慣れた形）
  const cells = useMemo(() => {
    const [y, m] = month.split('-').map(Number) as [number, number];
    const first = new Date(Date.UTC(y, m - 1, 1)).getUTCDay();
    const days = new Date(Date.UTC(y, m, 0)).getUTCDate();
    return [...Array<null>(first).fill(null), ...Array.from({ length: days }, (_, i) => `${month}-${String(i + 1).padStart(2, '0')}`)];
  }, [month]);
  const byDay = useMemo(() => {
    const m = new Map<string, ScheduleItem[]>();
    for (const s of items ?? []) m.set(s.date, [...(m.get(s.date) ?? []), s]);
    return m;
  }, [items]);
  return (
    <div className="page" style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button className="btn sm" onClick={() => setSp({ month: shiftMonth(month, -1) })} aria-label="前の月">←</button>
        <b style={{ fontSize: 18 }}>{month.replace('-', ' 年 ')} 月</b>
        <button className="btn sm" onClick={() => setSp({ month: shiftMonth(month, 1) })} aria-label="次の月">→</button>
        {month !== ymOf(today) && <button className="btn sm" onClick={() => setSp({})}>今月</button>}
        <span style={{ flex: 1 }} />
        <Link to="/routines" className="btn" style={{ textDecoration: "none", color: "inherit" }}>定期便の登録</Link>
      </div>
      {err && <p className="err" role="alert">{err}</p>}
      <TomorrowCheck today={today} onDone={load} />
      <section className="panel" aria-label="月間スケジュール" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
        {WEEK.map((w, i) => <div key={w} style={{ padding: '8px 10px', fontSize: 12, fontWeight: 700, color: i === 0 ? 'var(--alert)' : i === 6 ? '#0b6bcb' : 'var(--sub, #6b6b6b)', borderBottom: '1px solid var(--line)' }}>{w}</div>)}
        {cells.map((d, i) => {
          if (!d) return <div key={`e${i}`} style={{ borderBottom: '1px solid var(--line)', borderRight: '1px solid var(--line)', minHeight: 96 }} />;
          const hol = isJpHoliday(d);
          const list = byDay.get(d) ?? [];
          return (
            <div key={d} aria-label={`${Number(d.slice(8))} 日`} style={{ borderBottom: '1px solid var(--line)', borderRight: '1px solid var(--line)', minHeight: 96, padding: 6, display: 'grid', gap: 3, alignContent: 'start', background: d === today ? '#f4fbf6' : undefined }}>
              <small style={{ fontWeight: 700, color: hol || new Date(`${d}T00:00:00Z`).getUTCDay() === 0 ? 'var(--alert)' : undefined }}>{Number(d.slice(8))}{hol ? '・祝' : ''}</small>
              {list.map((s) => (
                <button key={s.id} onClick={() => setOpen(s)} title="押すとその日だけ変えられます"
                  style={{ font: 'inherit', fontSize: 12, textAlign: 'left', border: s.routineId ? 0 : '1px dashed #999', borderLeft: `4px ${s.routineId ? 'solid' : 'dashed'} ${s.driverId ? color.get(s.driverId) ?? '#999' : 'var(--alert)'}`, background: s.driverId && !s.driverOff && !s.restShort ? 'var(--surface-2, #f1f1f1)' : '#fdecea', borderRadius: 6, padding: '3px 6px', cursor: 'pointer', textDecoration: s.status === 'skipped' ? 'line-through' : undefined, opacity: s.status === 'skipped' ? 0.6 : 1 }}>
                  <b className="num">{s.sendTime}</b> {s.routineId ? names.routine(s.routineId) : 'スポット便（予約）'}
                  <span style={{ display: 'block', color: s.driverId && !s.driverOff && !s.restShort ? undefined : 'var(--alert)' }}>{s.driverId ? names.driver(s.driverId) : '担当未定'}{s.driverOff ? '・休みの日' : ''}{s.restShort ? '・休息が足りない' : ''}{s.status === 'sent' ? '・送信済み' : s.status === 'skipped' ? '・休み' : ''}</span>
                  {lockedBy(s.driverId, s.date) && <span style={{ display: 'block', fontWeight: 700 }}>編集中：{names.staff(lockedBy(s.driverId, s.date))}</span>}
                </button>
              ))}
            </div>
          );
        })}
      </section>
      {items?.length === 0 && <p className="muted" style={{ margin: 0 }}>この月の予定はありません。「定期便の登録」から作れます。</p>}
      {open && <DayDialog item={open} drivers={drivers} today={today} onClose={() => setOpen(null)} onDone={() => { setOpen(null); void load(); }} />}
    </div>
  );
}

function DayDialog({ item, drivers, today, onClose, onDone }: { item: ScheduleItem; drivers: Driver[]; today: string; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const me = useMe();
  const names = useNames();
  const dir = useDirectory();
  const [f, setF] = useState({ status: item.status === 'skipped' ? 'skipped' as const : 'planned' as const, driverId: item.driverId ?? '', sendTime: item.sendTime });
  const [err, setErr] = useState('');
  const [stops, setStops] = useState<string>('');
  useEffect(() => { api.get<{ templates: Template[] }>('/v1/office/templates').then((r) => { const t = r.templates.find((x) => x.id === item.templateId); setStops(t ? t.stops.map((s) => `${s.kind === 'pickup' ? '積む' : '届ける'}：${dir.places.get(s.placeId)?.name ?? '—'}`).join(' → ') : ''); }).catch(() => {}); }, [item.templateId]);
  const fixed = item.status === 'sent' || item.date < today;
  // その日の変更を開いている間は、担当のその日の予定をロックする（仕様書「同時編集の防止」）
  const lock = useEditLock(!fixed && item.driverId ? lockKeys.driverDay(item.driverId, item.date) : null);
  const giveMarks = async (driverId: string) => {
    if (!me) return;
    const t = (await api.get<{ templates: Template[] }>('/v1/office/templates')).templates.find((x) => x.id === item.templateId);
    // その日だけの担当・今すぐ送るは、ふつうの印（終わったら配送アプリが消す）。見せ続ける印（keep）は定期便の担当だけ
    if (t) await showPlacesToDriver(me.officeId, driverId, t.stops.map((s) => s.placeId));
  };
  // 担当が変わったら、この予定のメモの宛先も変える（前の担当は読めなくなる）。定期便のメモは定期便の担当宛てなので、
  // その日だけ別の人が担当するときは、その日の予定にメモを写す
  const moveNotes = async (to: string | null) => {
    const t = (await api.get<{ templates: Template[] }>('/v1/office/templates')).templates.find((x) => x.id === item.templateId);
    const sch = scheduleNoteId(item.id);
    await rebindNotes([sch, ...Array.from({ length: t?.stops.length ?? 0 }, (_, i) => stopNoteId(sch, i))], to);
    if (item.routineId && to && me && !(await readNote(sch))) {
      const text = await readNote(routineNoteId(item.routineId));
      if (text) await putNote(sch, { officeId: me.officeId, kind: 'batch', text, driverId: to });
    }
  };
  const save = async () => {
    setErr('');
    const r = ScheduleUpdate.safeParse({ ...f, driverId: f.driverId || null });
    if (!r.success) { setErr(r.error.issues[0]?.message ?? '入力を確かめてください'); return; }
    try {
      await api.patch(`/v1/office/schedules/${item.id}`, r.data, { ifMatch: item.version });
      if (r.data.driverId && r.data.driverId !== item.driverId) await giveMarks(r.data.driverId).catch(() => toast('行き先の情報を担当に渡せませんでした。もう一度保存してください'));
      if ((r.data.driverId ?? null) !== item.driverId && me) await moveNotes(r.data.driverId ?? null).catch(() => toast('メモを新しい担当に渡せませんでした。もう一度保存してください'));
      toast('この日の予定を変えました'); onDone();
    } catch (e) { setErr(e instanceof HttpError ? e.message : '保存できませんでした'); }
  };
  const sendNow = async () => {
    setErr('');
    try {
      if (item.driverId) await giveMarks(item.driverId).catch(() => {});
      await api.post(`/v1/office/schedules/${item.id}/send`, {});
      toast(`${names.driver(item.driverId)}さんに送りました`); onDone();
    } catch (e) { setErr(e instanceof HttpError ? e.message : '送れませんでした'); }
  };
  return (
    <Dialog title={`${item.date.slice(5).replace('-', '/')} ${item.routineId ? names.routine(item.routineId) : '予約'}`} onClose={onClose}>
      {stops && <p className="muted" style={{ margin: 0, fontSize: 13 }}>{stops}</p>}
      {fixed ? <p style={{ margin: 0 }}>{item.status === 'sent' ? 'もう送った予定です。配送の一覧から付け替え・取り消しができます。' : '過ぎた日の予定は変えられません。'}</p> : <>
        <label><input type="checkbox" style={{ width: 'auto' }} checked={f.status === 'skipped'} onChange={(e) => setF({ ...f, status: e.target.checked ? 'skipped' : 'planned' })} /> この日は休みにする</label>
        <span style={{ display: 'flex', gap: 8 }}>
          <span className="field" style={{ flex: 1 }}><label htmlFor="sc-time">送る時刻</label><input id="sc-time" type="time" value={f.sendTime} onChange={(e) => setF({ ...f, sendTime: e.target.value })} /></span>
          <span className="field" style={{ flex: 2 }}><label htmlFor="sc-drv">担当</label>
            <select id="sc-drv" value={f.driverId} onChange={(e) => setF({ ...f, driverId: e.target.value })}>
              <option value="">未定</option>{drivers.map((d) => <option key={d.id} value={d.id}>{names.driver(d.id)}</option>)}
            </select></span>
        </span>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>この日だけの変更です。くり返しの元（定期便）は変わりません。</p>
      </>}
      <LockBanner lock={lock} what={`${names.driver(item.driverId)} の ${item.date.slice(5).replace('-', '/')} の予定`} />
      {err && <p className="err" role="alert">{err}</p>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button className="btn" onClick={onClose}>閉じる</button>
        {!fixed && item.date === today && item.status === 'planned' && item.driverId && <button className="btn" disabled={!lock.mine} onClick={() => void sendNow()}>今すぐ送る</button>}
        {!fixed && <button className="btn pri" disabled={!lock.mine} onClick={() => void save()}>保存する</button>}
      </div>
    </Dialog>
  );
}


/** 前日の「明日の予定の確認」（仕様書「割り振り漏れの検知」）：明日の予定を一覧で見て、確かめた人と時刻を残す */
function TomorrowCheck({ today, onDone }: { today: string; onDone: () => void }) {
  const toast = useToast();
  const names = useNames();
  const tomorrow = new Date(Date.parse(`${today}T00:00:00Z`) + 86_400_000).toISOString().slice(0, 10);
  const [list, setList] = useState<ScheduleItem[] | null>(null);
  const load = () => api.get<{ schedules: ScheduleItem[] }>(`/v1/office/schedules?month=${tomorrow.slice(0, 7)}`).then((r) => setList(r.schedules.filter((x) => x.date === tomorrow))).catch(() => {});
  useEffect(() => { void load(); }, [tomorrow]);
  useLive(['deliveries', 'alerts'], load);
  if (!list?.length) return null;
  const planned = list.filter((x) => x.status === 'planned');
  const problems = planned.filter((x) => !x.driverId || x.driverOff || x.restShort).length;
  const checked = list.every((x) => x.checkedAt);
  const check = async () => {
    try { await api.post('/v1/office/schedules/check', { date: tomorrow }); toast('明日の予定を確かめました'); void load(); onDone(); }
    catch (e) { toast(e instanceof HttpError ? e.message : '記録できませんでした'); }
  };
  return (
    <section className="panel" aria-label="明日の予定の確認" style={{ padding: 12, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
      <b>明日（{tomorrow.slice(5).replace('-', '/')}）の予定 {planned.length} 件</b>
      {problems > 0 ? <span className="err">うち {problems} 件は担当未定・休み・休息不足です</span> : <span className="muted">担当はすべて決まっています</span>}
      <span style={{ flex: 1 }} />
      {checked ? <small className="muted">確かめ済み（{names.staff(list.find((x) => x.checkedBy)?.checkedBy)}）</small>
        : <button className="btn pri" onClick={() => void check()}>明日の予定を確かめた</button>}
    </section>
  );
}
